import React, { useRef, useEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import { NearPhotosQuery, hash } from "./gql/NearPhotoQuery";
import { Viewer } from "@photo-sphere-viewer/core";
import { VirtualTourPlugin } from "@photo-sphere-viewer/virtual-tour-plugin";
import { GalleryPlugin } from "@photo-sphere-viewer/gallery-plugin";
import Container from "react-bootstrap/Container";
import Header from "./Header";
import { format, parseISO } from "date-fns";
import ViewerMap from "./ViewerMap";
import "@photo-sphere-viewer/core/index.css";
import "@photo-sphere-viewer/virtual-tour-plugin/index.css";
import "@photo-sphere-viewer/gallery-plugin/index.css";

const OpenViewer = () => {
  const search = window.location.search;
  const params = new URLSearchParams(search);
  //const url = new URL(window.location.href);

  const id = parseInt(params.get("id"));

  const instanceRef = useRef(null);

  const [photoInfo, setPhotoInfo] = useState({
    id: id,
    projectName: "",
    originalFilename: "",
    captureDate: null,
  });

  const [coords, setCoords] = useState({ x: 0, y: 0 });

  const { loading, error, data, refetch } = useQuery(NearPhotosQuery, {
    variables: { id },
    skip: !id,
    context: { headers: { "X-Trust-Key": hash } },
  });

  useEffect(() => {
    let viewer;
    if (instanceRef.current) {
      viewer = new Viewer({
        container: instanceRef?.current,
        loadingImg: "/logo192-round.png",
        panorama: "/blank.png",
        minFov: 10,
        maxFov: 120,
        navbar: ["zoom", "gallery"],
        plugins: [
          GalleryPlugin.withConfig({
            visibleOnLoad: false,
            thumbnailSize: { width: 240, height: 100 },
          }),
          VirtualTourPlugin.withConfig({
            positionMode: "gps",
            renderMode: "3d",
            dataMode: "server",
            showLinkTooltip: false,
            startNodeId: id,
            transitionOptions: () => ({
              rotateTo: viewer?.getPosition(),
              zoomTo: viewer?.getZoomLevel(),
            }),
            getNode: async (id) => {
              try {
                const res = await refetch({ id: parseInt(id) });
                const nodes = res.data.nearPhotos;
                const newNodes = {
                  id: nodes?.id,
                  caption: nodes?.caption,
                  panorama: nodes?.panorama,
                  sphereCorrection: nodes?.sphere,
                  gps: nodes?.gps,
                  links: nodes?.links,
                  data: nodes?.data,
                };

                const updated = {
                  id: nodes?.id,
                  projectName: nodes?.data?.project_name,
                  captureDate: nodes?.data?.photo_datetime,
                  originalFilename: nodes?.data?.original_filename,
                };

                const updateCoords = {
                  x: nodes?.gps[0],
                  y: nodes?.gps[1],
                };

                setCoords((prev) => ({ ...prev, ...updateCoords }));
                setPhotoInfo((prev) => ({ ...prev, ...updated }));

                return newNodes;
              } catch (error) {
                console.error(error);
              }
            },
          }),
        ],
      });

      const virtualTour = viewer.getPlugin(VirtualTourPlugin);
      const gallery = viewer.getPlugin(GalleryPlugin);

      if (data?.nearPhotos) gallery.setItems(data?.nearPhotos?.data?.gallery);

      virtualTour.addEventListener("node-changed", ({ node, data }) => {
        const updateCoords = {
          x: node?.gps[0],
          y: node?.gps[1],
        };

        setCoords((prev) => ({ ...prev, ...updateCoords }));
        const updated = {
          id: node?.id,
          projectName: node?.data?.project_name,
          captureDate: node?.data?.photo_datetime,
          originalFilename: node?.data?.original_filename,
        };
        setPhotoInfo((prev) => ({ ...prev, ...updated }));

        const url = new URL(window.location.href);
        url.searchParams.set("id", node?.id);
        window.history.pushState(null, "", url.toString());
        gallery.setItems(node?.data?.gallery);
      });
    }

    instanceRef.current = null;
  }, [data, instanceRef, id, photoInfo, refetch]);

  if (loading) return;

  if (error) return;

  return (
    <div
      className="p-0 m-0"
      style={{ height: "100vh", backgroundColor: "#5c5c5c" }}
    >
      <Header />
      <Container
        fluid
        className="p-0 m-0"
        style={{
          minHeight: "500px",
          maxHeight: "1018px",
          backgroundColor: "#5c5c5c",
          height: "1018px",
        }}
      >
        {data.nearPhotos !== null && (
          <div
            ref={instanceRef}
            style={{
              width: "100%",
              height: "90vh",
              top: "62px",
              maxHeight: "1018px",
            }}
          >
            <table
              className="px-2"
              style={{
                zIndex: 999,
                backgroundColor: "rgba(61, 61, 61, 0.5)",
                color: "white",
                position: "absolute",
                top: "min(82px)",
                right: "2%",
                width: "50%",
                maxWidth: "700px",
              }}
            >
              <tbody>
                <tr>
                  <td>{photoInfo.id}</td>
                  <td>{photoInfo.projectName}</td>

                  <td>{photoInfo.originalFilename}</td>
                  <td>
                    {photoInfo.captureDate &&
                      format(
                        parseISO(photoInfo.captureDate),
                        "MM/dd/yyyy HH:mm:ss a"
                      )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
        <ViewerMap coords={coords} />
      </Container>
    </div>
  );
};

export default OpenViewer;
